/**
 * Core whiteboard hook. Manages drawing, sync, and state for one student.
 * Supports both whiteboard and lesson overlay surfaces.
 *
 * Inputs: studentId, targetParticipantId, localParticipantId, callObject, isTutor
 * Outputs: canvas/container refs, tool/penColor/isActive state, getPointerHandlers(surface),
 *          handleReceive(message), clearAll, clearSurface
 *
 * Flow: Pointer events → convert to logical coords → update stroke refs → batch queue →
 *       send via Daily → other side receives → handleReceive → update refs → redraw
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  SURFACES,
  VALID_SURFACES,
  STROKE_COLORS,
  LOGICAL_WIDTH,
  LOGICAL_HEIGHT,
  MESSAGE_TYPES,
  WHITEBOARD_RATIO_DEFAULT,
  WHITEBOARD_RATIO_MIN,
  WHITEBOARD_RATIO_MAX,
  CURSOR_THROTTLE_MS,
} from './constants';
import { screenToLogical } from './coordinateUtils';
import { redrawAllStrokes } from './canvasUtils';
import { findStrokeAtPoint } from './hitTest';
import {
  createBatchQueue,
  serializeStroke,
  serializeErase,
  serializeClear,
  serializeLayout,
  serializeActivate,
  serializeCursor,
  isWithinSizeLimit,
} from './syncUtils';

const LOGICAL_DIMS = { width: LOGICAL_WIDTH, height: LOGICAL_HEIGHT };

/**
 * Generate a unique stroke ID.
 * @returns {string}
 */
function generateStrokeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {object} params
 * @param {string} params.studentId - Whose whiteboard (for routing incoming messages)
 * @param {string} params.targetParticipantId - Who to send messages to (the other in the pair)
 * @param {string} params.localParticipantId - Our session ID (sender)
 * @param {object} params.callObject - Daily call object for sendAppMessage
 * @param {boolean} params.isTutor - For stroke color
 * @param {{ current: object }} [params.tutorWhiteboardStoreRef] - Tutor-only: ref to persist strokes per student
 * @param {string} [params.persistedStudentId] - Tutor-only: current student session id for store key
 * @param {boolean} [params.isFocused] - Tutor-only: when false, persist strokes so they survive unfocus
 */
export function useWhiteboard({
  studentId,
  targetParticipantId,
  localParticipantId,
  callObject,
  isTutor = false,
  tutorWhiteboardStoreRef = null,
  persistedStudentId = null,
  isFocused = true,
}) {
  const [whiteboardOpen, setWhiteboardOpenState] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [tool, setTool] = useState('pen');
  const defaultPenColor = isTutor ? STROKE_COLORS.tutor : STROKE_COLORS.student;
  const [penColor, setPenColor] = useState(defaultPenColor);
  const [whiteboardRatio, setWhiteboardRatioState] = useState(WHITEBOARD_RATIO_DEFAULT);

  const whiteboardStrokesRef = useRef([]);
  const lessonStrokesRef = useRef([]);
  const whiteboardCanvasRef = useRef(null);
  const lessonCanvasRef = useRef(null);
  const whiteboardContainerRef = useRef(null);
  const lessonContainerRef = useRef(null);
  // Outer (un-transformed) wrapper of the lesson content — used for reliable
  // getBoundingClientRect() on iOS Safari where transform: scale() breaks it.
  const lessonOuterContainerRef = useRef(null);

  const currentStrokeRef = useRef({ strokeId: null, surface: null, points: [] });
  const batchQueueRef = useRef(null);
  const resizeObserversRef = useRef([]);
  const lastCursorSendRef = useRef(0);
  const remoteCursorTimeoutRef = useRef(null);

  const [remoteCursor, setRemoteCursor] = useState({ lesson: null, whiteboard: null });

  // Part 1: Ready gate - only use coordinates after layout has settled
  const lessonReadyRef = useRef(false);
  const whiteboardReadyRef = useRef(false);
  const lessonInitialSettledRef = useRef(false);
  const whiteboardInitialSettledRef = useRef(false);

  const getReadyRef = useCallback((surface) => {
    return surface === SURFACES.LESSON ? lessonReadyRef : whiteboardReadyRef;
  }, []);

  // --- Surface helpers (refs and coords by surface) ---
  /** Convert pointer event to logical coords for the given surface. Returns null if invalid or not ready. */
  const getLogicalPointFromEvent = useCallback((surface, e) => {
    const readyRef = getReadyRef(surface);
    if (!readyRef.current) return null;

    if (surface === SURFACES.LESSON) {
      // Use the OUTER un-transformed container to avoid an iOS Safari bug where
      // getBoundingClientRect() on an element with transform: scale() returns the
      // pre-transform CSS layout size (1000×800) instead of the visually scaled size.
      // We replicate the same scale formula used by ScaledLessonContent so the math stays consistent.
      const outer = lessonOuterContainerRef.current;
      if (!outer) return null;
      const outerRect = outer.getBoundingClientRect();
      if (outerRect.width <= 0 || outerRect.height <= 0) return null;
      const scale = Math.min(outerRect.width / LOGICAL_WIDTH, outerRect.height / LOGICAL_HEIGHT);
      if (scale <= 0) return null;
      const contentW = LOGICAL_WIDTH * scale;
      const contentH = LOGICAL_HEIGHT * scale;
      const contentLeft = outerRect.left + (outerRect.width - contentW) / 2;
      const contentTop = outerRect.top + (outerRect.height - contentH) / 2;
      return screenToLogical(e.clientX - contentLeft, e.clientY - contentTop, contentW, contentH);
    }

    // Whiteboard surface: container has no CSS transform so getBoundingClientRect() is reliable everywhere.
    const container = whiteboardContainerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return screenToLogical(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
  }, [getReadyRef]);

  const getStrokesRef = useCallback((surface) => {
    return surface === SURFACES.LESSON ? lessonStrokesRef : whiteboardStrokesRef;
  }, []);

  const getCanvasRef = useCallback((surface) => {
    return surface === SURFACES.LESSON ? lessonCanvasRef : whiteboardCanvasRef;
  }, []);

  const getPixelDimensions = useCallback((surface) => {
    const container = surface === SURFACES.LESSON
      ? lessonContainerRef.current
      : whiteboardContainerRef.current;
    if (!container) return null;
    const readyRef = getReadyRef(surface);
    if (!readyRef.current) return null;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) return null;
    return { width: w, height: h };
  }, [getReadyRef]);

  const redraw = useCallback((surface) => {
    const canvas = getCanvasRef(surface).current;
    const strokesRef = getStrokesRef(surface);
    if (!canvas || !strokesRef.current) return;

    const pixelDims = getPixelDimensions(surface);
    if (!pixelDims) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    requestAnimationFrame(() => {
      redrawAllStrokes(ctx, strokesRef.current, LOGICAL_DIMS, pixelDims);
    });
  }, [getCanvasRef, getStrokesRef, getPixelDimensions]);

  /** Tutor-only: persist current stroke refs to store so they survive focus switch / whiteboard close */
  const persistStrokesToStore = useCallback((options = {}) => {
    const { force = false } = options;
    if (!isTutor || !tutorWhiteboardStoreRef?.current || !persistedStudentId) return;
    const store = tutorWhiteboardStoreRef.current;
    const lesson = [...lessonStrokesRef.current];
    const whiteboard = [...whiteboardStrokesRef.current];
    const hasAnyCurrent = lesson.length > 0 || whiteboard.length > 0;
    const existing = store[persistedStudentId];
    const hasAnyExisting = !!(
      existing &&
      ((Array.isArray(existing.lesson) && existing.lesson.length > 0) ||
        (Array.isArray(existing.whiteboard) && existing.whiteboard.length > 0))
    );
    // Guard against stale/unfocused instances overwriting a valid store entry with empties.
    if (!force && !hasAnyCurrent && hasAnyExisting) return;
    store[persistedStudentId] = {
      lesson,
      whiteboard,
    };
  }, [isTutor, tutorWhiteboardStoreRef, persistedStudentId]);

  /** Tutor-only: restore stroke refs from persisted store for this student. */
  const hydrateStrokesFromStore = useCallback(() => {
    if (!isTutor || !tutorWhiteboardStoreRef?.current || !persistedStudentId) return false;
    const entry = tutorWhiteboardStoreRef.current[persistedStudentId];
    if (!entry) return false;
    lessonStrokesRef.current = Array.isArray(entry.lesson) ? [...entry.lesson] : [];
    whiteboardStrokesRef.current = Array.isArray(entry.whiteboard) ? [...entry.whiteboard] : [];
    return true;
  }, [isTutor, tutorWhiteboardStoreRef, persistedStudentId]);

  const sendMessage = useCallback(
    (payload) => {
      if (!callObject || !targetParticipantId || !payload) return;
      if (!isWithinSizeLimit(payload)) return;

      try {
        callObject.sendAppMessage(payload, targetParticipantId);
      } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[whiteboard] sendAppMessage error:', err);
        }
      }
    },
    [callObject, targetParticipantId]
  );

  const sendCursorPosition = useCallback(
    (surface, e) => {
      if (!isActive || !VALID_SURFACES.includes(surface)) return;
      const logical = getLogicalPointFromEvent(surface, e);
      if (!logical) return;
      const now = Date.now();
      if (now - lastCursorSendRef.current < CURSOR_THROTTLE_MS) return;
      lastCursorSendRef.current = now;
      const payload = serializeCursor({
        studentId,
        senderId: localParticipantId,
        surface,
        x: logical.x,
        y: logical.y,
        tool,
        visible: true,
      });
      if (payload) sendMessage(payload);
    },
    [isActive, studentId, localParticipantId, tool, getLogicalPointFromEvent, sendMessage]
  );

  const sendCursorHidden = useCallback(
    (surface) => {
      if (!VALID_SURFACES.includes(surface)) return;
      const payload = serializeCursor({
        studentId,
        senderId: localParticipantId,
        surface,
        x: 0,
        y: 0,
        tool: 'pen',
        visible: false,
      });
      if (payload) sendMessage(payload);
    },
    [studentId, localParticipantId, sendMessage]
  );

  const flushAndSendStroke = useCallback(
    (batch) => {
      if (!batch?.points?.length || !batch.strokeId || !batch.surface) return;
      const payload = serializeStroke({
        studentId,
        senderId: localParticipantId,
        strokeId: batch.strokeId,
        points: batch.points,
        timestamp: Date.now(),
        surface: batch.surface,
        color: penColor,
      });
      if (payload) sendMessage(payload);
    },
    [studentId, localParticipantId, penColor, sendMessage]
  );

  useEffect(() => {
    batchQueueRef.current = createBatchQueue({
      onFlush: (batch) => {
        flushAndSendStroke(batch);
      },
    });
    return () => {
      batchQueueRef.current?.cancel();
    };
  }, [flushAndSendStroke]);


  const handlePointerDown = useCallback(
    (surface, e) => {
      if (!isActive || !VALID_SURFACES.includes(surface)) return;

      sendCursorPosition(surface, e);

      const logical = getLogicalPointFromEvent(surface, e);
      if (!logical) return;

      if (tool === 'pen') {
        const strokeId = generateStrokeId();
        currentStrokeRef.current = {
          strokeId,
          surface,
          points: [[logical.x, logical.y]],
        };
        const strokesRef = getStrokesRef(surface);
        strokesRef.current = [
          ...strokesRef.current,
          {
            strokeId,
            points: [[logical.x, logical.y]],
            color: penColor,
            senderId: localParticipantId,
          },
        ];
        redraw(surface);
        persistStrokesToStore();
        batchQueueRef.current?.add(strokeId, surface, localParticipantId, logical.x, logical.y);
      } else if (tool === 'eraser') {
        const strokesRef = getStrokesRef(surface);
        const hitId = findStrokeAtPoint({ x: logical.x, y: logical.y }, strokesRef.current);
        if (hitId) {
          strokesRef.current = strokesRef.current.filter((s) => s.strokeId !== hitId);
          redraw(surface);
          persistStrokesToStore();
          const payload = serializeErase({
            studentId,
            senderId: localParticipantId,
            strokeIds: [hitId],
            surface,
          });
          if (payload) sendMessage(payload);
        }
      }
    },
    [isActive, tool, getStrokesRef, getLogicalPointFromEvent, penColor, localParticipantId, redraw, sendMessage, persistStrokesToStore, sendCursorPosition]
  );

  const handlePointerMove = useCallback(
    (surface, e) => {
      sendCursorPosition(surface, e);

      if (!isActive || tool !== 'pen') return;

      const current = currentStrokeRef.current;
      if (!current.strokeId || current.surface !== surface) return;

      const logical = getLogicalPointFromEvent(surface, e);
      if (!logical) return;

      const strokesRef = getStrokesRef(surface);
      const stroke = strokesRef.current.find((s) => s.strokeId === current.strokeId);
      if (stroke) {
        stroke.points.push([logical.x, logical.y]);
        redraw(surface);
        batchQueueRef.current?.add(current.strokeId, surface, localParticipantId, logical.x, logical.y);
      }
    },
    [isActive, tool, getStrokesRef, getLogicalPointFromEvent, localParticipantId, redraw, sendCursorPosition]
  );

  const handlePointerUp = useCallback(
    (surface) => {
      if (!isActive || tool !== 'pen') return;

      const current = currentStrokeRef.current;
      if (current.strokeId && current.surface === surface) {
        batchQueueRef.current?.flush();
        currentStrokeRef.current = { strokeId: null, surface: null, points: [] };
        persistStrokesToStore();
      }
    },
    [isActive, tool, persistStrokesToStore]
  );

  const getPointerHandlers = useCallback(
    (surface) => {
      if (!VALID_SURFACES.includes(surface)) return {};
      return {
        onPointerDown: (e) => {
          e.preventDefault();
          handlePointerDown(surface, e);
        },
        onPointerMove: (e) => handlePointerMove(surface, e),
        onPointerUp: () => handlePointerUp(surface),
        onPointerLeave: () => {
          handlePointerUp(surface);
          sendCursorHidden(surface);
        },
      };
    },
    [handlePointerDown, handlePointerMove, handlePointerUp, sendCursorHidden]
  );

  const clearSurface = useCallback(
    (surface) => {
      if (surface === SURFACES.LESSON) {
        lessonStrokesRef.current = [];
        redraw(SURFACES.LESSON);
      } else if (surface === SURFACES.WHITEBOARD) {
        whiteboardStrokesRef.current = [];
        redraw(SURFACES.WHITEBOARD);
      } else {
        whiteboardStrokesRef.current = [];
        lessonStrokesRef.current = [];
        redraw(SURFACES.WHITEBOARD);
        redraw(SURFACES.LESSON);
      }

      const payload = serializeClear({
        studentId,
        senderId: localParticipantId,
        surface: surface && surface !== '' ? surface : undefined,
      });
      if (payload) sendMessage(payload);
      persistStrokesToStore({ force: true });
    },
    [studentId, localParticipantId, sendMessage, redraw, persistStrokesToStore]
  );

  const clearAll = useCallback(() => {
    clearSurface();
  }, [clearSurface]);

  const handleReceive = useCallback(
    (message) => {
      const data = message?.data && typeof message.data === 'object' ? message.data : message;
      if (!data?.type) return;

      const msgStudentId = data.studentId;
      if (msgStudentId && msgStudentId !== studentId) return;

      if (data.type === MESSAGE_TYPES.STROKE) {
        const { strokeId, points, senderId, surface } = data;
        if (!strokeId || !Array.isArray(points) || points.length < 1 || !VALID_SURFACES.includes(surface)) return;

        const strokesRef = getStrokesRef(surface);
        const color = (typeof data.color === 'string' && data.color)
          ? data.color
          : (isTutor ? STROKE_COLORS.student : STROKE_COLORS.tutor);
        const validPoints = points.filter((p) => Array.isArray(p) && p.length >= 2);
        if (validPoints.length === 0) return;

        const existing = strokesRef.current.find((s) => s.strokeId === strokeId);
        if (existing) {
          existing.points.push(...validPoints);
        } else {
          strokesRef.current = [
            ...strokesRef.current,
            { strokeId, points: [...validPoints], color, senderId },
          ];
        }
        redraw(surface);
        persistStrokesToStore();
      } else if (data.type === MESSAGE_TYPES.ERASE) {
        const { strokeIds, surface } = data;
        if (!Array.isArray(strokeIds) || strokeIds.length < 1 || !VALID_SURFACES.includes(surface)) return;

        const strokesRef = getStrokesRef(surface);
        strokesRef.current = strokesRef.current.filter((s) => !strokeIds.includes(s.strokeId));
        redraw(surface);
        persistStrokesToStore();
      } else if (data.type === MESSAGE_TYPES.CLEAR) {
        if (data.surface && VALID_SURFACES.includes(data.surface)) {
          const strokesRef = getStrokesRef(data.surface);
          strokesRef.current = [];
          redraw(data.surface);
        } else {
          whiteboardStrokesRef.current = [];
          lessonStrokesRef.current = [];
          redraw(SURFACES.WHITEBOARD);
          redraw(SURFACES.LESSON);
        }
        persistStrokesToStore();
      } else if (data.type === MESSAGE_TYPES.CURSOR) {
        const { surface: s, x, y, tool: t, visible } = data;
        if (!VALID_SURFACES.includes(s)) return;
        if (remoteCursorTimeoutRef.current) {
          clearTimeout(remoteCursorTimeoutRef.current);
          remoteCursorTimeoutRef.current = null;
        }
        if (visible && typeof x === 'number' && typeof y === 'number' && (t === 'pen' || t === 'eraser')) {
          setRemoteCursor((prev) => ({ ...prev, [s]: { x, y, tool: t } }));
          remoteCursorTimeoutRef.current = setTimeout(() => {
            remoteCursorTimeoutRef.current = null;
            setRemoteCursor((prev) => ({ ...prev, [s]: null }));
          }, 1200);
        } else {
          setRemoteCursor((prev) => ({ ...prev, [s]: null }));
        }
      } else if (data.type === MESSAGE_TYPES.LAYOUT && !isTutor) {
        const r = Number(data.whiteboardRatio);
        if (Number.isFinite(r) && r >= WHITEBOARD_RATIO_MIN && r <= WHITEBOARD_RATIO_MAX) {
          setWhiteboardRatioState(r);
        }
      } else if (data.type === MESSAGE_TYPES.ACTIVATE && !isTutor) {
        const active = data.active === true;
        setWhiteboardOpenState(active);
        setIsActive(active);
      }
    },
    [studentId, isTutor, getStrokesRef, redraw, persistStrokesToStore]
  );

  useEffect(() => {
    return () => {
      if (remoteCursorTimeoutRef.current) {
        clearTimeout(remoteCursorTimeoutRef.current);
      }
    };
  }, []);

  const onLayoutDragEnd = useCallback(
    (ratio) => {
      const clamped = Math.max(WHITEBOARD_RATIO_MIN, Math.min(WHITEBOARD_RATIO_MAX, ratio));
      setWhiteboardRatioState(clamped);
      const payload = serializeLayout({
        studentId,
        senderId: localParticipantId,
        whiteboardRatio: clamped,
      });
      if (payload) sendMessage(payload);
    },
    [studentId, localParticipantId, sendMessage]
  );

  const setWhiteboardRatio = useCallback((ratio) => {
    const clamped = Math.max(WHITEBOARD_RATIO_MIN, Math.min(WHITEBOARD_RATIO_MAX, ratio));
    setWhiteboardRatioState(clamped);
  }, []);

  const setWhiteboardOpen = useCallback(
    (open) => {
      if (!open) {
        batchQueueRef.current?.flush();
        persistStrokesToStore();
      }
      setWhiteboardOpenState(open);
      setIsActive(open);
      if (isTutor && targetParticipantId) {
        const payload = serializeActivate({
          studentId,
          senderId: localParticipantId,
          active: open,
        });
        if (payload) sendMessage(payload);
      }
    },
    [isTutor, targetParticipantId, studentId, localParticipantId, sendMessage, persistStrokesToStore]
  );

  const setupResizeObserver = useCallback(
    (containerRef, surface) => {
      const container = containerRef?.current;
      if (!container) return;

      const canvas = getCanvasRef(surface).current;
      if (!canvas) return;

      const isLesson = surface === SURFACES.LESSON;
      const readyRef = isLesson ? lessonReadyRef : whiteboardReadyRef;
      const initialSettledRef = isLesson ? lessonInitialSettledRef : whiteboardInitialSettledRef;

      // Reset ready state when (re)setting up - will be set after first valid dimensions
      readyRef.current = false;

      const updateCanvasAndRedraw = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) return;

        // Part 4: Use 1:1 canvas resolution (CSS pixels) for coordinate consistency.
        // getBoundingClientRect/clientX/clientY use CSS pixels; no devicePixelRatio scaling.
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        readyRef.current = true;
        redraw(surface);
      };

      const observer = new ResizeObserver(() => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) return;

        if (!initialSettledRef.current) {
          initialSettledRef.current = true;
          requestAnimationFrame(() => {
            updateCanvasAndRedraw();
          });
        } else {
          updateCanvasAndRedraw();
        }
      });
      observer.observe(container);
      resizeObserversRef.current.push(observer);
    },
    [getCanvasRef, redraw]
  );

  useEffect(() => {
    lessonReadyRef.current = false;
    whiteboardReadyRef.current = false;
    lessonInitialSettledRef.current = false;
    whiteboardInitialSettledRef.current = false;

    resizeObserversRef.current.forEach((obs) => obs.disconnect());
    resizeObserversRef.current = [];

    if (!whiteboardOpen) return;

    const wb = whiteboardContainerRef.current;
    const les = lessonContainerRef.current;
    if (wb) setupResizeObserver(whiteboardContainerRef, SURFACES.WHITEBOARD);
    if (les) setupResizeObserver(lessonContainerRef, SURFACES.LESSON);

    return () => {
      lessonReadyRef.current = false;
      whiteboardReadyRef.current = false;
      lessonInitialSettledRef.current = false;
      whiteboardInitialSettledRef.current = false;
      resizeObserversRef.current.forEach((obs) => obs.disconnect());
      resizeObserversRef.current = [];
    };
  }, [setupResizeObserver, whiteboardOpen]);

  /* Group H: close whiteboard when tutor leaves (student side) */
  useEffect(() => {
    if (!isTutor && !targetParticipantId) {
      setWhiteboardOpenState(false);
      setIsActive(false);
    }
  }, [isTutor, targetParticipantId]);

  /* Tutor-only: hydrate stroke refs when refocusing this student so close/open restores immediately. */
  useEffect(() => {
    if (!hydrateStrokesFromStore()) return;
    redraw(SURFACES.LESSON);
    redraw(SURFACES.WHITEBOARD);
  }, [hydrateStrokesFromStore, persistedStudentId, redraw]);

  /* Tutor-only: when opening whiteboard, re-apply persisted refs in case another instance updated store. */
  useEffect(() => {
    if (!whiteboardOpen || !hydrateStrokesFromStore()) return;
    redraw(SURFACES.LESSON);
    redraw(SURFACES.WHITEBOARD);
  }, [whiteboardOpen, hydrateStrokesFromStore, redraw]);

  /* Tutor-only: when exiting focus (or when focused instance unmounts), persist strokes so they survive and are restored on re-focus */
  const prevIsFocusedRef = useRef(isFocused);
  useEffect(() => {
    if (!isTutor) return;
    const wasFocused = prevIsFocusedRef.current;
    prevIsFocusedRef.current = isFocused;
    if (wasFocused && !isFocused) {
      batchQueueRef.current?.flush();
      persistStrokesToStore();
    }
    return () => {
      if (isTutor && prevIsFocusedRef.current) {
        batchQueueRef.current?.flush();
        persistStrokesToStore();
      }
    };
  }, [isTutor, isFocused, persistStrokesToStore]);

  return {
    whiteboardCanvasRef,
    lessonCanvasRef,
    whiteboardContainerRef,
    lessonContainerRef,
    lessonOuterContainerRef,
    tool,
    setTool,
    penColor,
    setPenColor,
    isActive,
    setIsActive,
    whiteboardOpen,
    setWhiteboardOpen,
    clearAll,
    clearSurface,
    getPointerHandlers,
    handleReceive,
    whiteboardStrokesRef,
    lessonStrokesRef,
    whiteboardRatio,
    setWhiteboardRatio,
    onLayoutDragEnd,
    remoteCursor,
    persistStrokesToStore,
  };
}
